import { useEffect, useRef } from "react";

interface UseCloseOnScrollOptions {
    isOpen: boolean;
    onClose: () => void;
}

/**
 * Hook that closes a dropdown/popup when scrolling outside of it
 * @param isOpen - Whether the dropdown is currently open
 * @param onClose - Callback to close the dropdown
 * @returns ref to attach to the dropdown container element
 */
export const useCloseOnScroll = <T extends HTMLElement = HTMLDivElement>(options: UseCloseOnScrollOptions) => {
    const { isOpen, onClose } = options;
    const elementRef = useRef<T>(null);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleScroll = (event: Event) => {
            const target = event.target;
            if (target instanceof HTMLElement) {
                const isDropdownScroll = target.closest('[role="listbox"]') !== null;

                if (!isDropdownScroll) {
                    onClose();
                }
            }
        };

        const scrollParents: HTMLElement[] = [];
        const currentElement = elementRef.current;
        let element = currentElement?.parentElement;

        while (element) {
            if (element.scrollHeight > element.clientHeight || element === document.body) {
                scrollParents.push(element);
                element.addEventListener("scroll", handleScroll, { passive: true, capture: true });
            }
            element = element.parentElement;
        }

        window.addEventListener("scroll", handleScroll, { passive: true, capture: true });

        return () => {
            scrollParents.forEach((parent) => {
                parent.removeEventListener("scroll", handleScroll, { capture: true });
            });
            window.removeEventListener("scroll", handleScroll, { capture: true });
        };
    }, [isOpen, onClose]);

    return elementRef;
};
